import '../../../packages/neb-lit-components/src/components/controls/neb-button-action';
import '../../../packages/neb-lit-components/src/components/inputs/neb-textarea';
import '../../../packages/neb-lit-components/src/components/controls/neb-button-icon';

import { css, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import {
  openOverlay,
  OVERLAY_KEYS,
} from '../../../packages/neb-lit-components/src/utils/overlay-constants';
import { BILLING_NOTE_TYPES } from '../../../packages/neb-utils/constants';
import { parseDate } from '../../../packages/neb-utils/date-util';
import {
  capitalize,
  centsToCurrency,
} from '../../../packages/neb-utils/formatters';
import {
  CSS_SPACING,
  CSS_SMALL_SPACING,
  CSS_COLOR_GREY_2,
  CSS_COLOR_GREY_4,
  CSS_COLOR_GREY_1,
  CSS_COLOR_WHITE,
  CSS_COLOR_GREY_10,
  CSS_FONT_SIZE_HEADER,
  CSS_COLOR_HIGHLIGHT,
  CSS_COLOR_GREY_5,
  CSS_FONT_WEIGHT_BOLD,
} from '../../styles/index';
import {
  getDatesOfService,
  openClaimBillingNote,
} from '../../utils/notes-util';
import {
  NebSimpleForm,
  ELEMENTS as NEB_SIMPLE_FORM_ELEMENTS,
} from '../forms/neb-simple-form';

const DATE_FORMAT = 'MM/DD/YYYY';

const INVOICE_NOTE_DETAIL_ROWS = {
  invoiceNumber: {
    label: 'Invoice',
  },
  dosFrom: {
    label: 'Date of Service',
    formatter: item => getDatesOfService(item),
  },
};

const CLAIM_NOTE_DETAIL_ROWS = {
  claimNumber: {
    label: 'Claim',
  },
  totalChargeAmount: {
    label: 'Amount',
    formatter: ({ totalChargeAmount }) => centsToCurrency(totalChargeAmount),
  },
  insurancePayerName: {
    label: 'Payer',
  },
  dosFrom: {
    label: 'Date of Service',
    formatter: item => getDatesOfService(item),
  },
};

const CHARGE_NOTE_DETAIL_ROWS = {
  chargeNumber: {
    label: 'Charge',
  },
  code: {
    label: 'Procedure Code',
  },
  description: {
    label: 'Description',
  },
  dateOfService: {
    label: 'Date of Service',
    formatter: ({ dateOfService }) =>
      parseDate(dateOfService).format(DATE_FORMAT),
  },
};

const NOTE_GROUP_DETAIL_ROWS = {
  [BILLING_NOTE_TYPES.INVOICE]: INVOICE_NOTE_DETAIL_ROWS,
  [BILLING_NOTE_TYPES.CLAIM]: CLAIM_NOTE_DETAIL_ROWS,
  [BILLING_NOTE_TYPES.CHARGE]: CHARGE_NOTE_DETAIL_ROWS,
};

const MAX_NOTE_LENGTH = 100;

export const ELEMENTS = {
  ...NEB_SIMPLE_FORM_ELEMENTS,
  newNoteTextarea: { id: 'new-note-textarea' },
  addNoteButton: { id: 'add-note-button' },
  editNotesButton: { id: 'edit-notes-button' },
  editNoteIcon: { selector: '.edit-note-icon' },
  deleteNoteButton: { selector: '.delete-note-button' },
  expandNoteButton: { selector: '.expand-note-button' },
  noteGroupContent: { selector: '.note-group-content' },
  noteGroupDetailContainer: { selector: '.note-group-detail-container' },
  noteGroupDetailRows: { selector: '.note-group-detail-rows' },
  noteGroupDetailRow: { selector: '.note-group-detail-row' },
  noteGroupDetailRowLabel: { selector: '.note-group-detail-row-label' },
  noteGroupDetailRowValue: { selector: '.note-group-detail-row-value' },
  noteText: { selector: '.note-text' },
  truncatedNote: { selector: '.truncated-note' },
  expandedNote: { selector: '.expanded-note' },
  noteActionButtons: { selector: '.note-action-buttons' },
  noteActionButtonsRow: { selector: '.note-action-buttons--row' },
  noteActionButtonsColumn: { selector: '.note-action-buttons--column' },
  editNoteTextarea: { id: 'edit-note-textarea' },
};

class NebNotesColumn extends NebSimpleForm {
  static get properties() {
    return {
      model: { type: Object },
      __noteGroups: { type: Array },
      __newNote: { type: String },
      __expandedNotes: { type: Object },
      title: { type: String },
      includeEditNotesButton: {
        type: Boolean,
        reflect: true,
        attribute: 'include-edit-notes-button',
      },
      includeNoteActionButtons: {
        type: Boolean,
        reflect: true,
        attribute: 'include-note-action-buttons',
      },
      includeAddNoteButton: {
        type: Boolean,
        reflect: true,
        attribute: 'include-add-note-button',
      },
      __editingNote: { type: Object },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .notes-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
          padding: ${CSS_SPACING} 0 0;
          background: ${CSS_COLOR_WHITE};
          min-width: 0;
          border: 1px solid ${CSS_COLOR_GREY_2};
          height: 100%;
        }

        .notes-header {
          font-weight: bold;
          margin: 0 ${CSS_SPACING} ${CSS_SMALL_SPACING};
        }

        .notes-section-content {
          overflow-y: auto;
        }

        .note-group {
          background: ${CSS_COLOR_GREY_4};
          border-radius: 10px;
          font-size: 14px;
          display: flex;
          flex-direction: column;
          margin-top: ${CSS_SPACING};
          padding: ${CSS_SPACING};
          margin: 20px;
        }

        .note-group-detail {
          display: flex;
          flex-direction: column;
        }

        .note-group-detail-row {
          font-size: 14px;
        }

        .note-group-detail-rows {
          flex: 1 1 0;
          min-width: 0;
        }

        .note-group-content {
          margin-top: ${CSS_SMALL_SPACING};
          font-size: 14px;
          background: ${CSS_COLOR_WHITE};
          padding: ${CSS_SMALL_SPACING};
          border-radius: 10px;
          border: 1px solid ${CSS_COLOR_GREY_2};
          position: relative;
          transition: all 0.2s ease-in-out;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: ${CSS_SMALL_SPACING};
        }

        .note-group-content.truncatable:hover {
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          cursor: pointer;
          background-color: ${CSS_COLOR_GREY_10};
        }

        .delete-note-button {
          position: static;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .delete-note-button-icon {
          fill: ${CSS_COLOR_GREY_5};
        }

        .note-group-content:hover .delete-note-button,
        .note-group-content:hover .expand-note-button {
          opacity: 1;
        }

        .note-group-detail-container {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          padding-bottom: ${CSS_SPACING};
          gap: ${CSS_SMALL_SPACING};
          flex-wrap: wrap;
        }

        .new-note-section {
          display: flex;
          flex-direction: column;
          gap: ${CSS_SMALL_SPACING};
          margin: ${CSS_SPACING};
        }

        .add-note-button {
          align-self: flex-end;
        }

        @keyframes fadeInSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .note-group-content {
          animation: fadeInSlideUp 0.3s ease-out;
        }

        .container-header {
          display: grid;
          grid-template-columns: 1fr min-content;
        }

        .text-header {
          font-size: ${CSS_FONT_SIZE_HEADER};
          font-weight: bold;
        }

        .icon-close {
          cursor: pointer;
          width: 24px;
          height: 24px;
          fill: ${CSS_COLOR_GREY_1};
        }

        .truncated-note {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          display: block;
          transition: all 0.2s ease-in-out;
          height: 1.4em;
        }

        .note-group-content:hover .truncated-note {
          height: 2.4em;
          padding-bottom: 20px;
        }

        .expand-note-button {
          position: absolute;
          bottom: 6px;
          right: 6px;
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
        }

        .expand-chevron {
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          opacity: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          fill: ${CSS_COLOR_GREY_1};
          transition: opacity 0.1s ease-in-out;
        }

        .note-group-content.truncatable:hover .expand-chevron,
        .note-group-content .expand-chevron.expanded {
          opacity: 1;
          transition: opacity 0.2s ease-in-out;
        }

        .expand-chevron.expanded {
          transform: translateX(-50%) rotate(180deg);
          fill: ${CSS_COLOR_HIGHLIGHT};
        }

        .note-text {
          word-break: break-word;
          height: 20px;
        }

        .edit-notes-button {
          align-self: center;
        }

        .note-text.expanded {
          white-space: pre-wrap;
          height: auto;
          max-height: none;
          padding-bottom: 20px;
        }

        .note-group-detail-row-label {
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .no-note-groups {
          place-self: center;
          align-content: center;
          height: 100%;
          font-size: 20px;
          margin-bottom: 40px;
        }

        .empty-note-group {
          font-style: italic;
          color: ${CSS_COLOR_GREY_5};
          justify-content: center;
          background: ${CSS_COLOR_GREY_10};
        }

        .edit-note-icon {
          position: static;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .note-group-content:hover .edit-note-icon {
          opacity: 1;
        }

        .edit-note-button-icon {
          fill: ${CSS_COLOR_GREY_5};
        }

        .edit-note-textarea {
          width: 100%;
          font-size: 14px;
          border-radius: 8px;
          box-sizing: border-box;
          height: 150px;
        }

        .note-action-buttons {
          opacity: 0;
          display: none;
          transition: opacity 0.2s ease-in-out;
          z-index: 2;
          gap: 5px;
        }

        .note-group-content:hover .note-action-buttons {
          opacity: 1;
          display: flex;
        }

        .note-action-buttons--row {
          flex-direction: row;
        }

        .note-action-buttons--column {
          flex-direction: column-reverse;
        }
      `,
    ];
  }

  initState() {
    super.initState();

    this.model = {
      noteGroups: [],
    };

    this.__newNote = '';
    this.__noteGroups = [];
    this.__expandedNotes = {};

    this.title = '';
    this.includeEditNotesButton = false;
    this.includeNoteActionButtons = false;
    this.includeAddNoteButton = false;

    this.onSave = () => {};

    this.onFetchNotes = () => {};

    this.__editingNote = null;
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      cancel: () => {
        this.__cancel();
      },

      changeNewNote: e => {
        this.__newNote = e.value;
      },

      addNote: async () => {
        if (!this.__newNote.trim()) return;

        const newEntry = {
          id: '',
          note: this.__newNote,
          sortOrder: this.__noteGroups[0].entries.length,
        };

        this.__noteGroups[0].entries = [
          ...this.__noteGroups[0].entries,
          newEntry,
        ];

        this.__noteGroups = [...this.__noteGroups];
        this.__newNote = '';

        await this.updateComplete;

        const container = this.renderRoot.querySelector(
          '.notes-section-content',
        );

        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      },

      handleKeyDown: e => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();

          this.handlers.addNote();
        }
      },

      deleteNote: (e, noteGroupIndex, noteIndex) => {
        this.__editingNote = null;
        const { entries } = this.__noteGroups[noteGroupIndex];

        this.__noteGroups[noteGroupIndex] = {
          ...this.__noteGroups[noteGroupIndex],
          entries: entries.filter((_, index) => index !== noteIndex),
        };

        this.__noteGroups = [...this.__noteGroups];

        if (this.__isNoteExpanded(noteGroupIndex, noteIndex)) {
          this.__toggleNoteExpansion(noteGroupIndex, noteIndex);
        }
      },

      openNotesOverlay: async noteGroup => {
        let overlayResult;

        if (this.type === BILLING_NOTE_TYPES.CLAIM) {
          overlayResult = await openClaimBillingNote(noteGroup.item);
        } else {
          overlayResult = await openOverlay(OVERLAY_KEYS.BILLING_NOTE, {
            parentType: this.type,
            parentId: noteGroup.item.id,
            parentData: {
              ...noteGroup.item,
              dateOfService: parseDate(noteGroup.item.dateOfService).format(
                DATE_FORMAT,
              ),
            },

            patientId: noteGroup.item.patientId,
          });
        }

        if (overlayResult) {
          this.onFetchNotes();
        }
      },

      toggleNoteExpansion: (noteGroupIndex, noteIndex, event) => {
        event.stopPropagation();

        this.__toggleNoteExpansion(noteGroupIndex, noteIndex);
      },

      startEditNote: async (e, noteGroupIndex, noteIndex, note) => {
        e.preventDefault();

        this.__isStartingEdit = true;
        this.__editingNote = { noteGroupIndex, noteIndex, value: note };

        await this.updateComplete;

        const textarea = this.shadowRoot.getElementById(
          ELEMENTS.editNoteTextarea.id,
        );

        if (textarea) {
          await textarea.updateComplete;

          const textbox = textarea.shadowRoot.getElementById('textbox');
          await textbox.updateComplete;

          const input = textbox.querySelector('#input');

          input.focus();
          input.selectionStart = input.value.length;
          input.selectionLength = 0;
        }

        this.__isStartingEdit = false;
      },

      changeEditNote: e => {
        if (this.__editingNote) {
          this.__editingNote = {
            ...this.__editingNote,
            value: e.value,
          };
        }
      },

      saveEditNote: e => {
        if (!this.__editingNote) return;

        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const { noteGroupIndex, noteIndex, value } = this.__editingNote;

          if (value.trim() !== '') {
            this.__noteGroups[noteGroupIndex].entries[noteIndex].note = value;
            this.__noteGroups = [...this.__noteGroups];
          }

          this.__editingNote = null;
        }
      },

      buttonMouseDown: e => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      },

      cancelEditNote: () => {
        if (this.__isStartingEdit) return;

        this.__editingNote = null;
      },
    };
  }

  createSelectors() {
    return {
      noteGroups: {
        stateKey: '__noteGroups',
      },
    };
  }

  async save() {
    const saveBody = {
      parentId: this.__noteGroups[0].item.id,
      parentType: this.type,
      patientId: this.__noteGroups[0].item.patientId,
      entries: this.__noteGroups[0].entries,
    };

    this.saving = true;

    await this.onSave(saveBody);

    this.saving = false;
  }

  willUpdate(changedProps) {
    if (changedProps.has('model') && this.isDirty && !this.saving) {
      return;
    }

    super.willUpdate(changedProps);
  }

  __cancel() {
    this.__noteGroups = this.initialState.__noteGroups;
  }

  __shouldTruncateNote(note) {
    return note.includes('\n') || note.length > MAX_NOTE_LENGTH;
  }

  __isNoteExpanded(noteGroupIndex, noteIndex) {
    const noteKey = `${noteGroupIndex}-${noteIndex}`;
    return this.__expandedNotes[noteKey] || false;
  }

  __isNoteBeingEdited(noteGroupIndex, noteIndex) {
    return (
      this.__editingNote &&
      this.__editingNote.noteGroupIndex === noteGroupIndex &&
      this.__editingNote.noteIndex === noteIndex
    );
  }

  __toggleNoteExpansion(noteGroupIndex, noteIndex) {
    const noteKey = `${noteGroupIndex}-${noteIndex}`;

    this.__expandedNotes = {
      ...this.__expandedNotes,
      [noteKey]: !this.__expandedNotes[noteKey],
    };
  }

  __renderNewNoteSection() {
    return html`<div class="new-note-section">
      <neb-textarea
        id="${ELEMENTS.newNoteTextarea.id}"
        class="container-input"
        .label=${'New Note'}
        .value="${this.__newNote}"
        .onChange="${this.handlers.changeNewNote}"
        @keydown="${this.handlers.handleKeyDown}"
      ></neb-textarea>

      <neb-button-action
        id="${ELEMENTS.addNoteButton.id}"
        class="add-note-button"
        icon="check"
        label="Add Note"
        .onClick="${this.handlers.addNote}"
        ?disabled="${!this.__newNote.trim()}"
      ></neb-button-action>
    </div>`;
  }

  __renderDeleteNoteButton(noteGroupIndex, noteIndex) {
    return html`<div
      class="delete-note-button"
      @click=${e => this.handlers.deleteNote(e, noteGroupIndex, noteIndex)}
      @mousedown=${this.handlers.buttonMouseDown}
    >
      <neb-button-icon
        icon="neb:close"
        class="delete-note-button-icon"
      ></neb-button-icon>
    </div>`;
  }

  __renderExpandNoteChevron(isExpanded, shouldTruncate) {
    if (!shouldTruncate) return nothing;

    return html` <div class="expand-chevron ${isExpanded ? 'expanded' : ''}">
      <neb-icon
        icon="neb:chevron"
        size="small"
        style="height: 16px; width: 16px;"
      ></neb-icon>
    </div>`;
  }

  __renderEditNoteIcon(noteGroupIndex, noteIndex, note) {
    return html`<div
      class="edit-note-icon"
      id="${ELEMENTS.editNoteIcon.id}-${noteGroupIndex}-${noteIndex}"
      @click=${e =>
        this.handlers.startEditNote(e, noteGroupIndex, noteIndex, note)}
      @mousedown=${this.handlers.buttonMouseDown}
      title="Edit Note"
    >
      <neb-button-icon
        icon="neb:correctClaim"
        class="edit-note-button-icon"
      ></neb-button-icon>
    </div>`;
  }

  __renderNoteGroupDetails(noteGroup, index) {
    return html` <div class="note-group-detail-container">
      <div class="note-group-detail-rows">
        ${Object.entries(NOTE_GROUP_DETAIL_ROWS[this.type]).map(
          ([key, { label, formatter }]) => {
            if (!noteGroup.item[key]) {
              return null;
            }

            return html`<div class="note-group-detail-row">
              <span class="note-group-detail-row-label"> ${label}: </span>
              <span class="note-group-detail-row-value">
                ${formatter ? formatter(noteGroup.item) : noteGroup.item[key]}
              </span>
            </div>`;
          },
        )}
      </div>

      ${this.includeEditNotesButton
        ? html`
            <neb-button-action
              id="${ELEMENTS.editNotesButton.id}"
              class="edit-notes-button"
              icon="pencil"
              label="Edit Notes"
              .onClick="${() => {
                this.handlers.openNotesOverlay(noteGroup, index);
              }}"
            ></neb-button-action>
          `
        : ''}
    </div>`;
  }

  __renderNoteGroupNotes(noteGroup, index) {
    if (noteGroup.entries.length === 0) {
      return html`<div class="note-group-content empty-note-group">
        No Notes
      </div>`;
    }
    return html`${noteGroup.entries.map((entry, noteIndex) =>
      this.__renderSingleNote(entry, index, noteIndex),
    )}`;
  }

  __renderSingleNote(entry, noteGroupIndex, noteIndex) {
    const { note } = entry;
    const shouldTruncate = this.__shouldTruncateNote(note);
    const isExpanded = this.__isNoteExpanded(noteGroupIndex, noteIndex);
    const isEditing = this.__isNoteBeingEdited(noteGroupIndex, noteIndex);

    const noteTextClass = classMap({
      'note-text': true,
      'truncated-note': shouldTruncate && !isExpanded,
      expanded: shouldTruncate && isExpanded,
    });

    return html`<div
      class="note-group-content ${shouldTruncate ? 'truncatable' : ''}"
      @click=${shouldTruncate
        ? e => this.handlers.toggleNoteExpansion(noteGroupIndex, noteIndex, e)
        : nothing}
    >
      ${isEditing
        ? this.__renderEditingNote(noteGroupIndex, noteIndex)
        : html`<div class="${noteTextClass}">${note}</div>
            ${this.__renderExpandNoteChevron(isExpanded, shouldTruncate)}
            ${this.__renderNoteActionButtons(
              note,
              noteGroupIndex,
              noteIndex,
              isExpanded,
            )}`}
    </div>`;
  }

  __renderEditingNote() {
    return html` <neb-textarea
      id="${ELEMENTS.editNoteTextarea.id}"
      class="edit-note-textarea"
      .value="${this.__editingNote.value}"
      .onChange="${this.handlers.changeEditNote}"
      @keydown="${this.handlers.saveEditNote}"
      @blur="${this.handlers.cancelEditNote}"
    ></neb-textarea>`;
  }

  __renderNoteActionButtons(note, noteGroupIndex, noteIndex, isExpanded) {
    if (!this.includeNoteActionButtons) return nothing;

    const buttonOrientation = isExpanded ? 'column' : 'row';

    return html`<div
      class="note-action-buttons note-action-buttons--${buttonOrientation}"
    >
      ${this.__renderEditNoteIcon(noteGroupIndex, noteIndex, note)}
      ${this.__renderDeleteNoteButton(noteGroupIndex, noteIndex)}
    </div>`;
  }

  __renderNoteGroup(noteGroup, index) {
    return html`
      <div class="note-group">
        ${this.__renderNoteGroupDetails(noteGroup, index)}
        ${this.__renderNoteGroupNotes(noteGroup, index)}
      </div>
    `;
  }

  __renderNoNotes() {
    return html`<div class="no-note-groups">No ${capitalize(this.type)}s</div>`;
  }

  render() {
    return html`
      <div class="notes-section">
        <div class="notes-header">${this.title}</div>

        ${this.__noteGroups.length > 0
          ? html`
              <div class="notes-section-content">
                ${this.__noteGroups.map((noteGroup, index) =>
                  this.__renderNoteGroup(noteGroup, index),
                )}
              </div>
            `
          : this.__renderNoNotes()}
        ${this.includeAddNoteButton ? this.__renderNewNoteSection() : ''}
        ${this.renderFooter()}
      </div>
    `;
  }
}

customElements.define('neb-notes-column', NebNotesColumn);
